import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Loader } from '@app/components';
import { sendVerificationCode, verifyCode, setPassword } from '@app/api';
import { IconArrowBack } from '@app/icons';
import Logo from '../SignIn/logo.png';
import { useForm } from 'forms/hooks';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Eye, EyeOff } from 'lucide-react';

declare global {
  interface Window {
    grecaptcha: {
      render: (container: string | HTMLElement, parameters: any) => number;
      getResponse: (widgetId?: number) => string;
      reset: (widgetId?: number) => void;
      ready: (callback: () => void) => void;
    };
  }
}

interface RegisterNewFormProps {
  onSuccess: (accessToken: string) => void;
  onClickSignIn: () => void;
  onCancel?: () => void;
}

export interface RegisterNewFormData {
  phone: string;
  code: string;
  password: string;
  confirmPassword: string;
  captchaToken: string | null;
}

const RegisterNewForm: FC<RegisterNewFormProps> = ({
  onSuccess,
  onClickSignIn,
  onCancel,
}) => {
  const [step, setStep] = useState<number>(0); // 0: phone, 1: code, 2: password
  const [pending, setPending] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [displayPhone, setDisplayPhone] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const resendInterval = useRef<NodeJS.Timeout | null>(null);

  const phoneRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const captchaWidgetId = useRef<number | null>(null);
  const captchaTokenRef = useRef<string | null>(null);

  const { values, onChange, errors, setErrors } = useForm<RegisterNewFormData>({
    phone: '',
    code: '',
    password: '',
    confirmPassword: '',
    captchaToken: null,
  });

  useEffect(() => {
    if (step === 0) {
      phoneRef.current?.focus();
    } else if (step === 1) {
      codeRef.current?.focus();
    } else if (step === 2) {
      passwordRef.current?.focus();
    }
  }, [step]);

  useEffect(() => {
    const value = values.phone || '';
    let formatted = '';
    if (value.length > 0) formatted = '(' + value.substring(0, 3);
    if (value.length >= 4) formatted += ') ' + value.substring(3, 6);
    if (value.length >= 7) formatted += '-' + value.substring(6, 8);
    if (value.length >= 9) formatted += '-' + value.substring(8, 10);
    setDisplayPhone(formatted);
  }, [values.phone]);

  useEffect(() => {
    if (step === 1 && resendTimer > 0) {
      resendInterval.current = setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (resendInterval.current) clearTimeout(resendInterval.current);
    };
  }, [resendTimer, step]);

  const handleCaptchaChange = useCallback((token: string | null) => {
    captchaTokenRef.current = token;
  }, []);

  const initializeCaptcha = useCallback(() => {
    if (window.grecaptcha && captchaRef.current && !captchaWidgetId.current) {
      if (captchaWidgetId.current !== null) {
        window.grecaptcha.reset(captchaWidgetId.current);
      }

      const widgetId = window.grecaptcha.render(captchaRef.current, {
        sitekey: '6Les8tsqAAAAAE1146YpRWz5mcMTWqNj1U-wZzpN',
        callback: handleCaptchaChange,
      });
      captchaWidgetId.current = widgetId;
    }
  }, [handleCaptchaChange]);

  useEffect(() => {
    if (step === 2) {
      if (window.grecaptcha) {
        window.grecaptcha.ready(initializeCaptcha);
      } else {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          window.grecaptcha.ready(initializeCaptcha);
        };
        document.head.appendChild(script);
      }
    }
  }, [step, initializeCaptcha]);

  useEffect(() => {
    return () => {
      if (captchaWidgetId.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(captchaWidgetId.current);
      }
    };
  }, []);

  const handlePhoneSubmit = async () => {
    if (values.phone.length !== 10) {
      setErrors({ phone: 'Введите корректный номер телефона' });
      return;
    }

    setPending(true);
    try {
      await sendVerificationCode({ phone: values.phone });
      setStep(1);
      setResendTimer(30);
      setErrors({});
    } catch (e: any) {
      setErrors({ phone: e.response?.data?.message || 'Произошла ошибка' });
    } finally {
      setPending(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (values.code.length !== 6) {
      setErrors({ code: 'Введите 6-значный код' });
      return;
    }

    setPending(true);
    try {
      await verifyCode({ phone: values.phone, code: values.code });
      setStep(2);
      setErrors({});
    } catch (e: any) {
      setErrors({ code: e.response?.data?.message || 'Неверный код' });
    } finally {
      setPending(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (values.password.length < 8) {
      setErrors({ password: 'Пароль должен содержать 8 или более символов' });
      return;
    }

    if (!/[a-z]/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 строчную букву',
      });
      return;
    }

    if (!/[A-Z]/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 заглавную букву',
      });
      return;
    }

    if (!/\d/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 цифру',
      });
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 специальный символ',
      });
      return;
    }

    if (values.password !== values.confirmPassword) {
      setErrors({ confirmPassword: 'Пароли не совпадают' });
      return;
    }

    const token = captchaTokenRef.current;
    if (!token) {
      setErrors({ password: 'Подтвердите, что вы не робот' });
      return;
    }

    setPending(true);
    try {
      const response = await setPassword({
        phone: values.phone,
        code: values.code,
        password: values.password,
      });
      onSuccess(response.accessToken);
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || 'Произошла ошибка';
      setErrors({ password: errorMessage });
    } finally {
      setPending(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer === 0) {
      setPending(true);
      try {
        await sendVerificationCode({ phone: values.phone });
        setResendTimer(30);
        setErrors({});
      } catch (e: any) {
        setErrors({ code: e.response?.data?.message || 'Произошла ошибка' });
      } finally {
        setPending(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setErrors({});
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto bg-white">
      <div className="min-h-full p-6 flex justify-center items-start">
        <div className="w-full max-w-[296px]">
          <div className="mb-6">
            {step > 0 && (
              <Button
                variant="ghost"
                className="flex items-center mb-8 px-4 py-2 h-10 rounded-lg"
                style={{ background: '#F2F8FD', color: '#3A57E8' }}
                type="button"
                onClick={handleBack}
                disabled={pending}
              >
                <IconArrowBack className="mr-1 w-4 h-4" />
                <span className="font-medium text-base">Назад</span>
              </Button>
            )}
            <div className="flex items-center gap-[5px]">
              <img src={Logo} className="w-[30.59px] h-[40px]" alt="logo" />
              <div className="flex flex-col justify-center">
                <h2 className="font-medium text-lg sm:text-[21px] leading-[130%] m-0 p-0 text-black">
                  SMR
                </h2>
                <p className="font-normal text-xs tracking-[0.25px] text-[#757575] m-0 p-0 leading-tight">
                  Поиск подряда и поставщиков
                </p>
              </div>
            </div>
          </div>

          <div className="w-full">
            <h1 className="font-medium text-lg sm:text-2xl text-[#424242] mb-4 sm:mb-6 leading-tight">
              {step === 0 && 'Введите номер телефона'}
              {step === 1 && 'Подтверждение кода'}
              {step === 2 && 'Придумайте пароль'}
            </h1>

            <form
              onSubmit={
                step === 2 ? handlePasswordSubmit : (e) => e.preventDefault()
              }
              className="flex flex-col gap-3"
            >
              {step === 0 && (
                <div className="flex flex-col gap-2">
                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex flex-col justify-center"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <label
                      htmlFor="phone"
                      className="text-xs mb-0"
                      style={{ color: '#757575' }}
                    >
                      Номер телефона
                    </label>
                    <input
                      id="phone"
                      type="text"
                      disabled={pending}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        value = value.slice(0, 10);
                        onChange(value, 'phone');
                      }}
                      value={displayPhone}
                      name="phone"
                      placeholder="+7 (___) __ - __ - __"
                      ref={phoneRef}
                      className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                      style={{
                        background: 'transparent',
                        color: '#0A0A0A',
                        lineHeight: '20px',
                      }}
                    />
                  </div>
                  {errors.phone && (
                    <span className="text-red-500 text-xs">{errors.phone}</span>
                  )}

                  <Button
                    type="button"
                    onClick={handlePhoneSubmit}
                    className="w-full h-[52px] rounded-lg mt-3"
                    disabled={values.phone.length !== 10 || pending}
                    style={{
                      background:
                        values.phone.length === 10 ? '#09345F' : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">
                        Получить код
                      </span>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={onClickSignIn}
                    className="mt-4 text-sm text-center underline"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#3A57E8',
                      cursor: 'pointer',
                    }}
                  >
                    Уже есть аккаунт? Войти
                  </button>
                </div>
              )}

              {step === 1 && (
                <div className="flex flex-col gap-3">
                  <div className="mb-2 text-sm" style={{ color: '#757575' }}>
                    Введите код из SMS, отправленного на наш номер
                  </div>

                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex flex-col justify-center"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <label
                      htmlFor="code"
                      className="text-xs mb-0"
                      style={{ color: '#757575' }}
                    >
                      Код подтверждения
                    </label>
                    <input
                      id="code"
                      type="text"
                      disabled={pending}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 6);
                        onChange(value, 'code');
                      }}
                      value={values.code}
                      name="code"
                      placeholder="123321"
                      ref={codeRef}
                      maxLength={6}
                      className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                      style={{
                        background: 'transparent',
                        color: '#0A0A0A',
                        lineHeight: '20px',
                      }}
                    />
                  </div>
                  {errors.code && (
                    <span className="text-red-500 text-xs">{errors.code}</span>
                  )}

                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendTimer > 0 || pending}
                    className="text-sm text-left underline"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: resendTimer > 0 || pending ? '#C3DFF7' : '#3A57E8',
                      cursor:
                        resendTimer > 0 || pending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {resendTimer > 0
                      ? `Не получили код? Отправить ещё раз через ${resendTimer} сек.`
                      : 'Не получили код? Отправить ещё раз'}
                  </button>

                  <Button
                    type="button"
                    onClick={handleCodeSubmit}
                    className="w-full h-[52px] rounded-lg"
                    disabled={values.code.length !== 6 || pending}
                    style={{
                      background:
                        values.code.length === 6 ? '#09345F' : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">Подтвердить</span>
                    )}
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="flex flex-col gap-3">
                  <p className="text-xs mb-3" style={{ color: '#757575' }}>
                    Пароль должен содержать минимум 8 символов, заглавные и
                    строчные буквы, цифры
                  </p>

                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex items-center justify-between"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <div className="flex flex-col flex-1">
                      <label
                        htmlFor="password"
                        className="text-xs"
                        style={{ color: '#757575' }}
                      >
                        Пароль
                      </label>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        disabled={pending}
                        onChange={(e) => onChange(e.target.value, 'password')}
                        value={values.password}
                        name="password"
                        placeholder="••••••••"
                        ref={passwordRef}
                        className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                        style={{
                          background: 'transparent',
                          color: '#0A0A0A',
                          lineHeight: '20px',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="ml-2"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {showPassword ? (
                        <EyeOff size={20} style={{ color: '#757575' }} />
                      ) : (
                        <Eye size={20} style={{ color: '#757575' }} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </span>
                  )}

                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex items-center justify-between mt-3"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <div className="flex flex-col flex-1">
                      <label
                        htmlFor="confirmPassword"
                        className="text-xs"
                        style={{ color: '#757575' }}
                      >
                        Повторите пароль
                      </label>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        disabled={pending}
                        onChange={(e) =>
                          onChange(e.target.value, 'confirmPassword')
                        }
                        value={values.confirmPassword}
                        name="confirmPassword"
                        placeholder="••••••••"
                        className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                        style={{
                          background: 'transparent',
                          color: '#0A0A0A',
                          lineHeight: '20px',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="ml-2"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} style={{ color: '#757575' }} />
                      ) : (
                        <Eye size={20} style={{ color: '#757575' }} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.confirmPassword}
                    </span>
                  )}

                  <div className="mt-2">
                    <div
                      ref={captchaRef}
                      className="w-full max-w-full overflow-hidden"
                    ></div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-[52px] rounded-lg mt-3"
                    disabled={
                      !values.password || !values.confirmPassword || pending
                    }
                    style={{
                      background:
                        values.password && values.confirmPassword
                          ? '#09345F'
                          : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">
                        Завершить регистрацию
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(RegisterNewForm);
